import _ from 'lodash';
import {Component} from "@angular/core";
import {HttpClient} from "@angular/common/http";
import {NotificationService} from "../../../../services/notificationService";
import {DICTIONARY} from "../../../../dictionary";
import {GeneralService} from "../../../../services/generalService";
import util from "util";

@Component({
	selector: "spf-record-checker-tool-component",
	templateUrl: "./spf-record-checker-tool.component.html",
})
export class SpfRecordCheckerToolComponent {
	constructor(private http: HttpClient,
				private ns:NotificationService,
				public gs:GeneralService) {}

	dic = DICTIONARY;
	domainTxt;
	checkSpfRecordInProcess;
	checkSpfRecordResult;
	scrollableContainer = document.getElementById('main');

	checkSpfRecord = () => {
		if (!this.domainTxt) {
			return;
		}

		const domainTxt = cleanDomain(this.domainTxt.trim());

		if (!isValidDomain(domainTxt)) {
			return this.ns.showWarnMessage(util.format(DICTIONARY.ERRORS.invalidDomain, this.domainTxt));
		}

		this.checkSpfRecordInProcess = true;
		this.checkSpfRecordResult = [];
		this.checkSpfRecordExecute(domainTxt, true);
	};

	checkSpfSubdomainRecord = (subdomainTxt) => {
		if (!subdomainTxt) {
			return;
		}

		subdomainTxt = cleanDomain(subdomainTxt);

		if (!isValidDomain(subdomainTxt)) {
			return this.ns.showWarnMessage(util.format(DICTIONARY.ERRORS.invalidDomain, subdomainTxt));
		}

		const alreadyCheckedSpfRecord = document.getElementById(subdomainTxt);
		if (alreadyCheckedSpfRecord) {
			const offsetTop = alreadyCheckedSpfRecord.offsetTop - this.scrollableContainer.offsetTop;
			this.scrollableContainer.scrollTo({
				top: offsetTop - 30,
				behavior: 'smooth'
			});
			this.highlightSelectedElement(alreadyCheckedSpfRecord);
			return;
		}

		this.checkSpfRecordExecute(subdomainTxt);
	};

	checkSpfRecordExecute(domainSearch, showNotFoundResult = false) {
		this.resolveTxt(domainSearch).subscribe((dnsResponse: any) => {
			if (!dnsResponse?.Answer || !Array.isArray(dnsResponse.Answer)) {
				this.checkSpfRecordInProcess = false;
				this.ns.showErrorMessage(this.dic.ERRORS.domainResolveFailure);
				return;
			}

			const txtRecords = [];
			dnsResponse.Answer.forEach(answer => {
				if (answer && typeof answer.data === "string") {
					let record = answer.data.trim();
					record = _.trim(record, '"');
					txtRecords.push(record);
				}
			});

			if (!txtRecords.length) {
				this.checkSpfRecordInProcess = false;
				if (showNotFoundResult) {
					this.ns.showWarnMessage(util.format(this.dic.ERRORS.noTxtRecordsFound, domainSearch));
				}
				return;
			}

			for (let i = 0; i < txtRecords.length; i++) {
				if (txtRecords[i].startsWith("v=spf1")) {
					const parsedSpfRecord = parseSpfRecord(txtRecords[i]);
					const spfRecordResult = {
						rootDomain: domainSearch,
						spfRecord: sanitizeSpecialChars(txtRecords[i]),
						items: parsedSpfRecord,
					};

					this.checkSpfRecordResult.push(spfRecordResult);
					this.checkSpfRecordInProcess = false;

					setTimeout(() => {
						this.scrollableContainer.scrollTo({ top: this.scrollableContainer.scrollHeight, behavior: 'smooth' });
					});
					return;
				}
			}

			this.checkSpfRecordInProcess = false;
			if (showNotFoundResult) {
				this.ns.showWarnMessage(util.format(this.dic.ERRORS.noSpfRecordFound, domainSearch));
			}
		}, err => {
			this.checkSpfRecordInProcess = false;
		});
	}

	resolveTxt(name) {
		const url = `https://cloudflare-dns.com/dns-query?name=${encodeURIComponent(name)}&type=TXT`;
		return this.http.get(url, { headers: { accept: "application/dns-json" } });
	}

	highlightSelectedElement(element: HTMLElement): void {
		element.classList.add('found-in-page');

		setTimeout(() => {
			element?.classList.remove('found-in-page');
		}, 1500);
	}
}

function parseSpfRecord(spfRecord) {
	const tokens = (spfRecord.match(/[^\s]+/gm) || []);
	const items = [];
	for (let token of tokens) {
		let qualifier = '+';
		if (token.startsWith('-')) qualifier = '-';
		else if (token.startsWith('~')) qualifier = '~';
		else if (token.startsWith('?')) qualifier = '?';

		const cleanToken = token.replace(/^[-~+?]/, '');
		let mechanism = cleanToken.split(':')[0].split('=')[0].split('/')[0];
		let value = '';
		if (cleanToken.includes(':')) value = cleanToken.split(':')[1];
		else if (cleanToken.includes('=')) value = cleanToken.split('=')[1];
		else if (cleanToken.includes('/')) value = cleanToken.split('/')[1];

		let description = '';
		if (mechanism === 'a') description = "Match if host found in the domain's A DNS records.";
		else if (mechanism === 'mx') description = "Match if host found in the domain's MX DNS records.";
		else if (mechanism === 'ip4' || mechanism === 'ip6') description = "Match if host found in the IP address(es).";
		else if (mechanism === 'include') description = "Match if host found in the provided domain's SPF record.";
		else if (mechanism === 'redirect') description = "Match if host found in the provided redirected domain's SPF record.";
		else if (mechanism === 'exists') description = "Match if the provided domain returns a result.";
		else if (mechanism === 'ptr') description = "Match if host found in the domain's PTR DNS records.";
		else if (mechanism === 'all' && qualifier !== "~" && qualifier !== "-") description = "Always matches (default fallback).";

		if (value === "spf1") {
			qualifier = '';
			description = "The SPF record version";
		}

		items.push({
			qualifier,
			qualifierDesc: value === "spf1" ? '' : getQualifierDesc(qualifier),
			mechanism,
			value,
			description
		});
	}
	return items;
}


function getQualifierDesc(qualifier) {
	switch (qualifier) {
		case "error":
			return "Qualifier spell wrong";
		case "+":
			return "Pass";
		case "-":
			return "Fail";
		case "~":
			return "SoftFail";
		case "?":
			return "Neutral";
		default:
			return "Pass";
	}
}

function sanitizeSpecialChars(v) {
	return v.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;")
			.replace(/'/g, "&#039;");
}

function cleanDomain(v) {
	const raw = (v || '').trim();
	const noProto = raw.replace(/^https?:\/\//i, '');
	const noPath = noProto.replace(/\/.*$/, '');
	return noPath;
}

function isValidDomain(v) {
	return /^\b((?=[a-z0-9-_]{1,63}\.)(xn--)?[a-z0-9_]+(-[a-z0-9]+)*\.)+[a-z]{2,63}\b/i.test(v);
}
